import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { 
  GetEmployeeQueryResponse, 
  GetEmployeesQueryResponse, 
  CreateEmployeeCommand, 
  UpdateEmployeeCommand, 
  CreateCommandResponse, 
  ClockInOutDto, 
  GetAttendanceReportQueryResponse, 
  GetTodayAttendanceQueryResponse,
  SalaryCalculationDto, 
  SalaryPaymentDto, 
  SalaryPaymentResponseDto 
} from '../interfaces/interfaces';

@Injectable({
  providedIn: 'root'
})
export class AttendanceService {
  private baseUrl: string;

  constructor(private http: HttpClient) {
    this.baseUrl = environment.apiSite.endsWith('/') 
      ? environment.apiSite.slice(0, -1) 
      : environment.apiSite;
  }

  // Employee Management
  getEmployee(id: number): Observable<GetEmployeeQueryResponse> {
    return this.http.get<GetEmployeeQueryResponse>(`${this.baseUrl}/api/Employee/${id}`);
  }

  getAllEmployees(isActive?: boolean): Observable<GetEmployeesQueryResponse> {
    const params = isActive !== undefined ? { isActive: isActive.toString() } : {};
    return this.http.get<GetEmployeesQueryResponse>(`${this.baseUrl}/api/Employee`, { params });
  }

  createEmployee(command: CreateEmployeeCommand): Observable<CreateCommandResponse> {
    return this.http.post<CreateCommandResponse>(`${this.baseUrl}/api/Employee`, command);
  }

  updateEmployee(command: UpdateEmployeeCommand): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/api/Employee`, command);
  }

  // Attendance Management
  clockInOut(dto: ClockInOutDto): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/Attendance/clock-in-out`, dto);
  }

  getAttendanceReport(employeeId?: number, startDate?: string, endDate?: string): Observable<GetAttendanceReportQueryResponse> {
    const params: any = {};
    if (employeeId) params.employeeId = employeeId.toString();
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    return this.http.get<GetAttendanceReportQueryResponse>(`${this.baseUrl}/api/Attendance/report`, { params });
  }

  getTodayAttendance(employeeId?: number): Observable<GetAttendanceReportQueryResponse> {
    const params = employeeId ? { employeeId: employeeId.toString() } : {};
    return this.http.get<GetAttendanceReportQueryResponse>(`${this.baseUrl}/api/Attendance/today`, { params });
  }

  getTodayAttendanceEntries(employeeId?: number): Observable<GetTodayAttendanceQueryResponse> {
    const params = employeeId ? { employeeId: employeeId.toString() } : {};
    return this.http.get<GetTodayAttendanceQueryResponse>(`${this.baseUrl}/api/Attendance/today`, { params });
  }

  // Salary Management
  calculateSalary(employeeId: number, year: number, month: number): Observable<SalaryCalculationDto> {
    return this.http.get<SalaryCalculationDto>(`${this.baseUrl}/api/Salary/calculate/${employeeId}`, {
      params: { year: year.toString(), month: month.toString() }
    });
  }

  getSalaryPayments(employeeId?: number, year?: number, month?: number, status?: number): Observable<SalaryPaymentResponseDto[]> {
    const params: any = {};
    if (employeeId) params.employeeId = employeeId.toString();
    if (year) params.year = year.toString();
    if (month) params.month = month.toString();
    if (status) params.status = status.toString();
    
    return this.http.get<SalaryPaymentResponseDto[]>(`${this.baseUrl}/api/Salary/payments`, { params });
  }

  createSalaryPayment(dto: SalaryPaymentDto): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/Salary/payments`, dto);
  }

  deleteSalaryPayment(id: number) {
    return this.http.delete(`${this.baseUrl}/api/Salary/payments/${id}`);
  }

  // Attendance Import
  importAttendanceRecords(records: any[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/Attendance/import`, { records });
  }

  // Individual Punch Import
  detectImportConflicts(records: any[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/Attendance/detect-import-conflicts`, { records });
  }

  importIndividualPunchRecords(records: any[], overwriteExistingData: boolean = false): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/Attendance/import-individual-punches`, { 
      records, 
      overwriteExistingData 
    });
  }

  // Monthly Attendance Report
  getMonthlyAttendanceReport(year: number, month: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/Attendance/monthly-report`, {
      params: { year: year.toString(), month: month.toString() }
    });
  }

  // Manual Attendance Entry
  createManualAttendance(attendance: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/Attendance/manual-attendance`, attendance);
  }

  // Monthly Manual Attendance Grid
  getMonthlyManualAttendance(employeeId: number, year: number, month: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/Attendance/monthly-manual-attendance`, {
      params: { 
        employeeId: employeeId.toString(), 
        year: year.toString(), 
        month: month.toString() 
      }
    });
  }

  saveMonthlyManualAttendance(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/Attendance/save-monthly-manual-attendance`, data);
  }
} 